export function cn(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: cn is not implemented yet.', args);
  return null;
}

/**
 * Sanitize a website URL returned by AI which may contain display names.
 * e.g. "Living Spaces (livingspaces.com)" -> "livingspaces.com"
 * e.g. "Amazon (amazon.com/dp/B08XYZ)"-> "amazon.com/dp/B08XYZ" * e.g."https://wayfair.com" -> "https://wayfair.com" (unchanged)
 */
export const sanitizeWebsiteUrl = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw?.trim();

  // If it already looks like a valid URL (starts with http/https or is a plain domain), return as-is
  if (/^https?:\/\//i?.test(trimmed)) return trimmed;

  // Extract URL from parentheses: "Display Name (domain.com)" -> "domain.com"
  const parenMatch = trimmed?.match(/\(([^)]+)\)/);
  if (parenMatch) {
    const inner = parenMatch?.[1]?.trim();
    // Validate it looks like a domain/URL
    if (/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+/?.test(inner)) {
      return inner;
    }
  }

  // If the string contains spaces, it's likely a display name — try to extract a domain-like token
  if (trimmed?.includes(' ')) {
    const tokens = trimmed?.split(/\s+/);
    const domainToken = tokens?.find(t => /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+/?.test(t));
    if (domainToken) return domainToken;
    // No valid domain found — return empty so fallback (Google search) is used
    return '';
  }

  return trimmed;
};

/**
 * Returns the lucide-react icon name string for a given purchaseType subcategory id.
 * Looks up the icon from the categories array defined in CategorySelect.jsx.
 * Returns null if not found.
 */
export const getPurchaseTypeIcon = (purchaseType) => {
  if (!purchaseType) return null;

  const categories = [
    {
      id: 'home-property',
      subcategories: [
        { id: 'home-purchase', icon: 'House' },
        { id: 'home-refinance', icon: 'RefreshCw' },
        { id: 'roofing', icon: 'Triangle' },
        { id: 'hvac', icon: 'Wind' },
        { id: 'plumbing', icon: 'Droplets' },
        { id: 'electrical', icon: 'Zap' },
        { id: 'painting', icon: 'Paintbrush' },
        { id: 'flooring', icon: 'Grid2X2' },
        { id: 'windows-doors', icon: 'DoorOpen' },
        { id: 'fencing', icon: 'Fence' },
        { id: 'landscaping', icon: 'Leaf' },
        { id: 'pest-control', icon: 'Bug' },
        { id: 'pool-installation', icon: 'Waves' },
        { id: 'solar-panels', icon: 'Sun' },
        { id: 'generators', icon: 'Bolt' }
      ]
    },
    {
      id: 'travel-experiences',
      subcategories: [
        { id: 'flights', icon: 'Plane' },
        { id: 'hotels', icon: 'Hotel' },
        { id: 'vacation-packages', icon: 'Palmtree' },
        { id: 'cruises', icon: 'Ship' },
        { id: 'rental-cars', icon: 'Car' },
        { id: 'travel-insurance', icon: 'ShieldCheck' },
        { id: 'concerts', icon: 'Music' },
        { id: 'sporting-events', icon: 'Trophy' },
        { id: 'courses', icon: 'BookOpen' },
        { id: 'gym-memberships', icon: 'Dumbbell' }
      ]
    },
    {
      id: 'vehicle-transportation',
      subcategories: [
        { id: 'car-purchase', icon: 'Car' },
        { id: 'motorcycle', icon: 'Bike' },
        { id: 'rv', icon: 'Caravan' },
        { id: 'boat', icon: 'Sailboat' },
        { id: 'car-insurance', icon: 'ShieldCheck' },
        { id: 'auto-repair', icon: 'Wrench' },
        { id: 'tires', icon: 'CircleDot' },
        { id: 'detailing', icon: 'Sparkles' },
        { id: 'body-shop', icon: 'Hammer' }
      ]
    },
    {
      id: 'tech-electronics',
      subcategories: [
        { id: 'computers-laptops', icon: 'Laptop' },
        { id: 'tablets', icon: 'Tablet' },
        { id: 'smartphones', icon: 'Smartphone' },
        { id: 'tv-home-theater', icon: 'Tv' },
        { id: 'smart-home-devices', icon: 'Home' },
        { id: 'audio-equipment', icon: 'Speaker' },
        { id: 'headphones', icon: 'Headphones' },
        { id: 'wearables', icon: 'Watch' }
      ]
    },
    {
      id: 'financial-professional',
      subcategories: [
        { id: 'credit-cards', icon: 'CreditCard' },
        { id: 'home-purchase', icon: 'House' },
        { id: 'home-refinance', icon: 'RefreshCw' },
        { id: 'accountants', icon: 'Calculator' },
        { id: 'attorneys', icon: 'Scale' },
        { id: 'financial-advisors', icon: 'TrendingUp' },
        { id: 'moving-companies', icon: 'Truck' },
        { id: 'tutors', icon: 'UserCheck' }
      ]
    },
    {
      id: 'life-events',
      subcategories: [
        { id: 'weddings', icon: 'Heart' },
        { id: 'engagement-rings', icon: 'Gem' },
        { id: 'birthdays', icon: 'PartyPopper' },
        { id: 'funerals', icon: 'Flower2' },
        { id: 'childcare', icon: 'Baby' },
        { id: 'baby-products', icon: 'Baby' },
        { id: 'private-school', icon: 'School' }
      ]
    },
    {
      id: 'insurance',
      subcategories: [
        { id: 'auto-insurance', icon: 'Car' },
        { id: 'homeowners-insurance', icon: 'House' },
        { id: 'renters-insurance', icon: 'KeyRound' },
        { id: 'life-insurance', icon: 'HeartHandshake' },
        { id: 'health-insurance', icon: 'Stethoscope' },
        { id: 'pet-insurance', icon: 'PawPrint' },
        { id: 'business-insurance', icon: 'Building2' }
      ]
    },
    {
      id: 'home-purchases',
      subcategories: [
        { id: 'major-appliances', icon: 'WashingMachine' },
        { id: 'furniture', icon: 'Sofa' },
        { id: 'mattresses', icon: 'BedDouble' },
        { id: 'home-gym', icon: 'Dumbbell' },
        { id: 'home-security', icon: 'ShieldCheck' },
        { id: 'lighting', icon: 'Lightbulb' },
        { id: 'rugs', icon: 'LayoutGrid' },
        { id: 'decor', icon: 'Sparkles' }
      ]
    },
    {
      id: 'education',
      subcategories: [
        { id: 'college', icon: 'GraduationCap' },
        { id: 'student-loans', icon: 'Banknote' },
        { id: 'bootcamps', icon: 'Code' },
        { id: 'certifications', icon: 'Award' },
        { id: 'online-learning', icon: 'MonitorPlay' },
        { id: 'professional-training', icon: 'BriefcaseBusiness' },
        { id: 'tutoring', icon: 'BookOpenCheck' }
      ]
    },
    {
      id: 'health-wellness',
      subcategories: [
        { id: 'health-insurance', icon: 'Stethoscope' },
        { id: 'dental-care', icon: 'Smile' },
        { id: 'vision-care', icon: 'Eye' },
        { id: 'mental-health', icon: 'Brain' },
        { id: 'physical-therapy', icon: 'PersonStanding' },
        { id: 'gym-fitness', icon: 'Dumbbell' },
        { id: 'personal-training', icon: 'Zap' },
        { id: 'nutrition-dietitian', icon: 'Apple' },
        { id: 'chiropractic', icon: 'Bone' },
        { id: 'medical-equipment', icon: 'Stethoscope' },
        { id: 'cosmetic-procedures', icon: 'Sparkles' },
        { id: 'weight-loss-programs', icon: 'Scale' }
      ]
    }
  ];

  for (const category of categories) {
    const sub = category?.subcategories?.find(s => s?.id === purchaseType);
    if (sub) return sub?.icon;
  }
  return null;
};
